import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField, SelectField } from '@/components/forms/Field';
import type { Student } from '@/types/domain';
import type { StudentPayload } from '@/services/api/endpoints/students';

const schema = z
  .object({
    // Optional: a blank number is issued by the server as STU-YYYY-#####.
    studentNumber: z.string().max(32),
    firstName: z.string().min(1, 'First name is required').max(100),
    middleName: z.string().max(100),
    lastName: z.string().min(1, 'Last name is required').max(100),
    dateOfBirth: z.string().min(1, 'Date of birth is required'),
    gender: z.enum(['MALE', 'FEMALE', 'OTHER', 'UNDISCLOSED']),
    email: z.string().email('Enter a valid e-mail address').or(z.literal('')),
    phone: z.string().max(32),
    bloodGroup: z.string().max(8),
    nationality: z.string().max(100),
    admissionDate: z.string().min(1, 'Admission date is required'),
    /*
     * Set on create only, and only to ACTIVE or INACTIVE — the two starting
     * states the server accepts. An edit carries no status at all: every later
     * move is a named lifecycle action with its own history row.
     */
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
  })
  // Mirrors the database check constraint, so the user is told before the
  // request rather than by a rejection afterwards.
  .refine((values) => values.dateOfBirth < values.admissionDate, {
    path: ['admissionDate'],
    message: 'Admission date must be after the date of birth',
  });

export type StudentFormValues = z.infer<typeof schema>;

const EMPTY: StudentFormValues = {
  studentNumber: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  gender: 'UNDISCLOSED',
  email: '',
  phone: '',
  bloodGroup: '',
  nationality: '',
  admissionDate: '',
  status: 'ACTIVE',
};

export function toPayload(values: StudentFormValues): StudentPayload {
  const blankToUndefined = (value: string) => (value === '' ? undefined : value);
  return {
    ...values,
    studentNumber: blankToUndefined(values.studentNumber),
    middleName: blankToUndefined(values.middleName),
    email: blankToUndefined(values.email),
    phone: blankToUndefined(values.phone),
    bloodGroup: blankToUndefined(values.bloodGroup),
    nationality: blankToUndefined(values.nationality),
  };
}

export function toFormValues(student: Student): StudentFormValues {
  return {
    studentNumber: student.studentNumber,
    firstName: student.firstName,
    middleName: student.middleName ?? '',
    lastName: student.lastName,
    dateOfBirth: student.dateOfBirth,
    gender: student.gender,
    email: student.email ?? '',
    phone: student.phone ?? '',
    bloodGroup: student.bloodGroup ?? '',
    nationality: student.nationality ?? '',
    admissionDate: student.admissionDate,
    // Left out on purpose: the profile update refuses status changes.
    status: undefined,
  };
}

interface StudentFormModalProps {
  open: boolean;
  student: Student | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: StudentFormValues, setError: ReturnType<typeof useForm<StudentFormValues>>['setError']) => void;
}

/**
 * The student form, shared by the create and edit paths.
 *
 * Deliberately carries no grade or section field: placement belongs to an
 * academic year and is created through enrolment. Putting it here would invite
 * exactly the "current grade column" modelling this system avoids.
 */
export function StudentFormModal({ open, student, saving, onClose, onSubmit }: StudentFormModalProps) {
  const form = useForm<StudentFormValues>({
    resolver: zodResolver(schema),
    values: student === null ? EMPTY : toFormValues(student),
  });

  const submit = form.handleSubmit((values) => onSubmit(values, form.setError));
  const { errors } = form.formState;

  return (
    <Modal
      open={open}
      size="lg"
      title={student === null ? 'Register a student' : `Edit ${student.fullName}`}
      description="Personal details only. Placement into a grade is recorded as an enrolment."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            Save
          </Button>
        </>
      }
    >
      <form className="space-y-4" noValidate onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Student number"
            hint="Leave blank to issue the next number automatically"
            error={errors.studentNumber?.message}
            {...form.register('studentNumber')}
          />
          {student === null && (
            <SelectField label="Status" error={errors.status?.message} {...form.register('status')}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </SelectField>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <TextField label="First name" required error={errors.firstName?.message} {...form.register('firstName')} />
          <TextField label="Middle name" error={errors.middleName?.message} {...form.register('middleName')} />
          <TextField label="Last name" required error={errors.lastName?.message} {...form.register('lastName')} />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            label="Date of birth"
            type="date"
            required
            error={errors.dateOfBirth?.message}
            {...form.register('dateOfBirth')}
          />
          <SelectField label="Gender" required error={errors.gender?.message} {...form.register('gender')}>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
            <option value="UNDISCLOSED">Prefer not to say</option>
          </SelectField>
          <TextField
            label="Admission date"
            type="date"
            required
            error={errors.admissionDate?.message}
            {...form.register('admissionDate')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="E-mail" type="email" error={errors.email?.message} {...form.register('email')} />
          <TextField label="Phone" error={errors.phone?.message} {...form.register('phone')} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Blood group"
            hint="e.g. O+"
            error={errors.bloodGroup?.message}
            {...form.register('bloodGroup')}
          />
          <TextField label="Nationality" error={errors.nationality?.message} {...form.register('nationality')} />
        </div>
      </form>
    </Modal>
  );
}
